import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { collection, query, where, getDocs, limit, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import './BlogPost.css'; // This will be replaced in the next step
import './ContentRenderer.js';
import ContentRenderer from './ContentRenderer.js';

// const ContentRenderer = ({ htmlContent, images = [] }) => {
//   let imageIndex = 0;

//   const options = {
//     replace: (domNode) => {
//       if (domNode.attribs?.['data-image-placeholder']) {
//         if (imageIndex < images?.length) {
//           const imageUrl = images[imageIndex];
//           imageIndex++;

//           return (
//             <img
//               src={imageUrl}
//               alt={`Blog content ${imageIndex}`}
//               className="inline-article-image"
//               loading="lazy"
//             />
//           );
//         }

//         return null;
//       }
//     }
//   };

//   return <>{parse(htmlContent || "", options)}</>;
// };

const BlogPost = () => {
  // Hooks for data and state management
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Reading Progress Bar Logic - works the same
  useEffect(() => {
    const handleScroll = () => {
      const docElement = document.documentElement;
      const scrollPosition = docElement.scrollTop;
      const totalHeight = docElement.scrollHeight - docElement.clientHeight;
      if (totalHeight > 0) {
        setScrollProgress((scrollPosition / totalHeight) * 100);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Main Data Fetching Logic (Main post + Related posts)
  useEffect(() => {
    const fetchAllData = async () => {
      if (!slug) return;
      setLoading(true);

      try {
        const postsRef = collection(db, 'blog_posts');

        // Query 1: Fetch the main post where 'slug' matches the URL
        const mainPostQuery = query(postsRef, where("slug", "==", slug), limit(1));
        const mainPostSnapshot = await getDocs(mainPostQuery);

        let mainPostData = null;
        if (!mainPostSnapshot.empty) {
          mainPostData = mainPostSnapshot.docs[0].data();
          setPost(mainPostData);
        } else {
          setLoading(false);
          return; // Post not found, stop here
        }

        // Query 2: Fetch 4 recent posts that are NOT the current one for showing in sidebar
        const recentPostsQuery = query(postsRef, orderBy("date", "desc"), limit(5));
        const recentPostsSnapshot = await getDocs(recentPostsQuery);

        // 2b. Use JavaScript to filter out the *current* post from the results
        const filteredRelatedPosts = recentPostsSnapshot
          .docs
          .filter(doc => doc.data().slug !== slug);

        // If we successfully filtered it, we will have 4 a piece from one document in the list to be sliced
        setRelatedPosts(filteredRelatedPosts.map(doc => ({ id: doc.id, ...doc.data() })).slice(0, 4));

      } catch (error) {
        console.error("Failed to fetch blog data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
    window.scrollTo(0, 0); // Scroll to top on load
  }, [slug]);

  // SKELETON LOADER
  if (loading) {
    return (
      <>
        <div className="progress-bar-container"><div className="progress-bar-fill" style={{ width: `0%` }}></div></div>
        <div className="skeleton-container">
          <main className="main-article-content skeleton-main"><div className="skeleton title"></div><div className="skeleton text large"></div><div className="skeleton image"></div><div className="skeleton text"></div><div className="skeleton text"></div></main>
          <aside className="blog-sidebar skeleton-sidebar"><div className="skeleton text"></div><div className="skeleton text"></div></aside>
        </div>
      </>
    );
  }

  // NOT FOUND HANDLER
  if (!post) {
    return <div className="container" style={{ padding: '100px', textAlign: 'center' }}>404 - Article Not Found</div>;
  }

  const formattedDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <>
      <Helmet>
        {/* Use the values from the post (or fallback defaults) */}
        <title>
          {post?.title ? `${post.title} | Qinto` : "Qinto Hub | Smart Selling Tips"}
        </title>
        <meta name="description" content={post?.metaDescription || "Expert guides on buying and selling used goods."} />
        {/* Open Graph – for social sharing */}
        <meta property="og:title" content={post?.title} />
        <meta property="og:description" content={post?.metaDescription} />
        <meta property="og:image" content={post?.ogImage || post.image} />
        <meta property="og:url" content={post?.canonicalUrl || window.location.href} />
        <meta property="og:type" content="article" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post?.title} />
        <meta name="twitter:description" content={post?.metaDescription} />
        <meta name="twitter:image" content={post?.ogImage || post.image} />
        {/* Schema.org JSON‑LD */}
        <script type="application/ld+json">
          {JSON.stringify(post?.schemaOrg || {})}
        </script>
        {/* Canonical URL */}
      </Helmet>

      {/* The progress bar remains fixed at the top */}
      <div className="progress-bar-container">
        <div className="progress-bar-fill" style={{ width: `${scrollProgress}%` }}></div>
      </div>

      {/* Container for the 2-column layout */}
      <div className="blog-post-grid-container">

        {/* ---------- MAIN CONTENT (LEFT) ----------- */}
        <main className="main-article-content">
          <div className="article-header">
            <Link to="/blog" className="back-to-hub">← All Articles</Link>
            <span className="article-category">{post.category}</span>
            <h1>{post.title}</h1>
            <p className="article-excerpt">{post.excerpt}</p>
            <div className="article-meta">Published on {formattedDate(post.date)}</div>
          </div>

          {post.image && (
            <img src={post.image} alt={post.title} loading="lazy" className="article-main-image" />
          )}

          <article className={`article-body ${post.image ? 'has-image' : ''}`}>

            <ContentRenderer htmlContent={post.content} images={post.inlineImages}/>
            
            
          </article>

          {/* <div className="article-body" dangerouslySetInnerHTML={{ __html: post.content }} /> */}
        </main>

        {/*
        * 
      * SIDEBAR (RIGHT - RELATED CONTENT)
      *
          */}
        <aside className="blog-sidebar">
          <h3>Related Articles</h3>
          {relatedPosts.map(relatedPost => (
            <Link to={`/blog/${relatedPost.slug}`} key={relatedPost.id} className="blog-sidebar-post">
              {relatedPost.image && (
                <img className="blog-sidebar-image" src={relatedPost.image} alt={relatedPost.title} loading="lazy" />
              )}
              <div className="blog-sidebar-post-text">
                <h4>{relatedPost.title}</h4>
                <span className="blog-sidebar-post-excerpt">{relatedPost.excerpt}</span>
              </div>
            </Link>
          ))}

          {/* <div className="blog-sidebar-cta-box">
            <h4>Sell Better on Qinto</h4>
            <p>Use AI to get your listing live in under 30 seconds.</p>
            <a href="https://play.google.com/store/apps/details?id=com.qinto.qintoapp" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">Get App</a>
          </div> */}

          <img
                src="/assets/qinto_download_Square_Banner.png"
                alt="Download Banner"
                className="download-banner"
              />

        </aside>
      </div>
    </>
  );
};

export default BlogPost;